import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Badge } from './ui/badge';
import { LoadingSpinner } from './layout/SharedComponents';
import api from '../services/api';
import eyvoLogo from '../assets/eyvo_logo.png';
import {
  Building2, MapPin, Mail, Globe, Hash,
  Users, Shield, Package, Layers, Calendar,
  CheckCircle, Clock, AlertTriangle, Star,
} from 'lucide-react';

const MODULE_LABELS = {
  requisitions:    'Purchase Requisitions',
  orders:          'Purchase Orders',
  rfq:             'RFQ / RFI Sourcing',
  contracts:       'Contract Management',
  goods_receiving: 'Receiving & AP',
  assets:          'Asset Management',
  inventory:       'Inventory Control',
  expenses:        'Employee Expenses',
  supplier_portal: 'Supplier Portal',
  sales_orders:    'Sales Order Processing',
};

const MODULE_CATEGORY = {
  requisitions: 'Procurement',  orders: 'Procurement',
  rfq: 'Sourcing',              contracts: 'Contracts',
  goods_receiving: 'Finance',   assets: 'Finance',
  inventory: 'Warehouse',       expenses: 'Finance',
  supplier_portal: 'Suppliers', sales_orders: 'Sales',
};

const PLAN_COLOURS = {
  starter:      'bg-gray-100 text-gray-700 border-gray-200',
  professional: 'bg-blue-100 text-blue-700 border-blue-200',
  enterprise:   'bg-purple-100 text-purple-700 border-purple-200',
  trial:        'bg-amber-100 text-amber-700 border-amber-200',
};

const STATUS_COLOURS = {
  active:    'text-green-600',
  trial:     'text-amber-600',
  suspended: 'text-red-600',
};

function InfoRow({ icon: Icon, label, value, mono = false }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className={`text-sm mt-0.5 ${mono ? 'font-mono' : 'font-medium'}`}>{value}</p>
      </div>
    </div>
  );
}

export default function AboutModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/api/system/about')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'America/Los_Angeles' }); }
    catch { return d; }
  };

  // Group modules by category
  const modulesByCategory = {};
  (data?.modules || []).forEach(m => {
    const cat = MODULE_CATEGORY[m] || 'Other';
    if (!modulesByCategory[cat]) modulesByCategory[cat] = [];
    modulesByCategory[cat].push(m);
  });

  // Licence utilisation
  const licenceUsed  = data?.licensed_users || 0;
  const licenceLimit = data?.license_limit  || 0;
  const licencePct   = licenceLimit > 0 ? Math.min(100, (licenceUsed / licenceLimit) * 100) : 0;
  const licenceColour = licencePct >= 90 ? 'bg-red-500' : licencePct >= 75 ? 'bg-amber-500' : 'bg-green-500';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header banner */}
        <div className="flex items-center gap-4 px-6 py-5 border-b"
          style={{ backgroundColor: 'hsl(var(--sidebar))' }}>
          <img src={eyvoLogo} alt="Eyvo eProcurement"
            className="h-10 w-auto flex-shrink-0"
            style={{ filter: 'brightness(0) invert(1)' }} />
          <div>
            <h2 className="text-white font-bold text-base" style={{ fontFamily: 'Space Grotesk' }}>
              About Eyvo eProcurement
            </h2>
            {data?.current_version && (
              <p className="text-white/60 text-xs mt-0.5">
                Version <span className="font-mono">{data.current_version}</span>
              </p>
            )}
          </div>
          {data?.plan && (
            <span className={`ml-auto capitalize border rounded-full px-3 py-1 text-xs font-semibold ${PLAN_COLOURS[data.plan] || PLAN_COLOURS.professional}`}>
              {data.plan} Plan
            </span>
          )}
        </div>

        {loading ? (
          <div className="py-16"><LoadingSpinner /></div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground text-center py-10">Could not load information.</p>
        ) : (
          <div className="px-6 py-5 space-y-7">

            {/* ── Company Information ── */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Organisation
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow icon={Building2} label="Company Name"   value={data.company_name} />
                <InfoRow icon={Hash}      label="Registration No" value={data.registration_number} mono />
                {(data.company_address || data.company_city || data.company_country) && (
                  <div className="col-span-2 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Address</p>
                      <p className="text-sm font-medium mt-0.5">
                        {[data.company_address, data.company_city, data.company_country].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}
                <InfoRow icon={Mail}  label="Finance Email" value={data.finance_email} />
                <InfoRow icon={Globe} label="Website"       value={data.website} />
                <InfoRow icon={Hash}  label="Tax / VAT No"  value={data.tax_id} mono />
                <InfoRow icon={Globe} label="System Currency" value={data.system_currency} mono />
              </div>
            </section>

            {/* ── Licence ── */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Licence
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</span>
                  </div>
                  <p className={`text-lg font-bold capitalize ${STATUS_COLOURS[data.status] || 'text-foreground'}`}>
                    {data.status === 'active' && <CheckCircle className="inline h-4 w-4 mr-1 -mt-0.5" />}
                    {data.status === 'trial'  && <Clock className="inline h-4 w-4 mr-1 -mt-0.5" />}
                    {data.status === 'suspended' && <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />}
                    {data.status}
                  </p>
                  {data.activated_at && (
                    <p className="text-xs text-muted-foreground mt-1">Since {fmtDate(data.activated_at)}</p>
                  )}
                </div>
                <div className="border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {data.status === 'trial' ? 'Trial Expires' : 'Licence Expiry'}
                    </span>
                  </div>
                  <p className="text-lg font-bold">
                    {data.trial_ends_at ? fmtDate(data.trial_ends_at) : '—'}
                  </p>
                  {!data.trial_ends_at && data.status === 'active' && (
                    <p className="text-xs text-muted-foreground mt-1">Contact support@eyvo.com to renew</p>
                  )}
                </div>
              </div>

              {/* User licence utilisation bar */}
              <div className="border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">User Licences</span>
                  </div>
                  <span className="text-sm font-bold">
                    {licenceUsed} <span className="text-muted-foreground font-normal">of</span> {licenceLimit}
                  </span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${licenceColour}`}
                    style={{ width: `${licencePct}%` }}
                    role="progressbar"
                    aria-valuenow={licenceUsed}
                    aria-valuemax={licenceLimit}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{licenceUsed} licensed · {data.active_users} active · {data.total_users} total users</span>
                  <span>{(licenceLimit - licenceUsed)} remaining</span>
                </div>
              </div>
            </section>

            {/* ── Enabled Modules ── */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Enabled Modules <span className="ml-1 normal-case font-normal">({data.modules?.length || 0} active)</span>
              </h3>
              {Object.keys(modulesByCategory).length === 0 ? (
                <p className="text-sm text-muted-foreground">No module information available.</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(modulesByCategory).map(([category, mods]) => (
                    <div key={category}>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {category}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {mods.map(m => (
                          <div key={m}
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-green-800">
                              {MODULE_LABELS[m] || m}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Footer ── */}
            <div className="border-t pt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>Powered by <strong>Eyvo eProcurement</strong></span>
              <span>
                <a href="mailto:support@eyvo.com" className="text-primary hover:underline">support@eyvo.com</a>
                {data.current_version && (
                  <span className="ml-3 font-mono">v{data.current_version}</span>
                )}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
